import * as React from 'react';
import { Menu, useResourceDefinitions, useSidebarState } from 'react-admin';
import {
  PeopleAltOutlined,
  StorefrontOutlined,
  ReceiptLongOutlined,
  HistoryOutlined,
  LocalShippingOutlined,
  AppRegistrationOutlined,
  CampaignOutlined,
  WhatsApp,
  MailOutlineOutlined,
  Close,
  LocalOfferOutlined,
  PercentOutlined,
  DashboardCustomizeOutlined,
  QrCode2Outlined,
  PaymentsOutlined,
} from '@mui/icons-material';
import { Box, Typography, ListSubheader, IconButton } from '@mui/material';
import { useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hasRole, isAdminRole } from '../../utils/roles';
import { useIsMobileSidebar } from '../../hooks/useIsMobileSidebar';
import { useOwnRestaurant } from '../../hooks/useOwnRestaurant';
import { MenuCardPencilIcon } from '../icons/MenuCardPencilIcon';

/**
 * Cofa stan aktywny narzucony przez `RaMenuItemLink-active`.
 *
 * `MenuItemLink` sam liczy swoje podswietlenie przez `useMatch({ path: to })`
 * bez `end`, wiec dopasowuje sie do calego poddrzewa trasy — `/locationsManagement`
 * zapala sie takze na `/locationsManagement/:id/items`. Tam wlasnie prowadzi skrot
 * "Edytuj menu", wiec swiecily sie oba wpisy naraz. RA nie wystawia zadnego propa,
 * ktorym da sie to dopasowanie zawezic, a klasy `active` nie da sie zdjac — wiec
 * na tej jednej trasie przywracamy wyglad spoczynkowy z motywu.
 *
 * `&&` podwaja klase elementu, dzieki czemu regula wygrywa z `.RaMenuItemLink-active`
 * zdefiniowana wyzej na kontenerze <Menu> specyficznoscia, a nie kolejnoscia
 * wstrzykniecia stylow przez emotion.
 */
const resetActiveSx = {
  '&&.RaMenuItemLink-active': {
    backgroundColor: 'transparent !important',
    color: 'var(--color-text-secondary) !important',
    boxShadow: 'none',
    '& .RaMenuItemLink-icon': { color: 'var(--color-text-muted) !important' },
    '&:hover': {
      backgroundColor: 'var(--color-primary-soft) !important',
      color: 'var(--color-primary) !important',
    },
  },
};

/**
 * Odwrotnosc `resetActiveSx`: listy rabatow i kodow rabatowych nie maja wlasnych
 * pozycji w menu — wchodzi sie do nich przez "Rabaty" (`/discount-types`).
 * `MenuItemLink` nie zapali sie na cudzej trasie, wiec nadajemy mu wyglad aktywny recznie.
 */
const forceActiveSx = {
  backgroundColor: 'var(--color-primary-soft) !important',
  color: 'var(--color-primary-active) !important',
  boxShadow: 'inset 3px 0 0 0 var(--color-primary)',
  '& .RaMenuItemLink-icon': { color: 'var(--color-primary) !important' },
};

const RESOURCE_META: Record<string, { icon: React.ReactElement }> = {
  members: { icon: <PeopleAltOutlined /> },
  locationsManagement: { icon: <StorefrontOutlined /> },
  orders: { icon: <ReceiptLongOutlined /> },
  orderHistory: { icon: <HistoryOutlined /> },
  coupons: { icon: <LocalOfferOutlined /> },
  discounts: { icon: <PercentOutlined /> },
  variantTemplates: { icon: <DashboardCustomizeOutlined /> },
};

export const MyMenu = () => {
  const resources = useResourceDefinitions();
  const { t } = useTranslation();
  const isMobile = useIsMobileSidebar();
  const [, setSidebarOpen] = useSidebarState();
  const isAdminOrSuperAdmin = isAdminRole();
  const canSendPromotion = hasRole('ADMIN', 'SUPER_ADMIN', 'RESTAURANT_OWNER', 'VENDOR');
  // RESTAURANT_OWNER / VENDOR maja tylko jedna restauracje, wiec zamiast kazac im
  // wchodzic w liste lokalizacji i klikac ikone karty menu, dajemy skrot prosto
  // do ich menu. Dla pozostalych rol `restaurant` jest nullem i skrot nie istnieje.
  const { restaurant } = useOwnRestaurant();
  const isOnMenuEditor = !!useMatch('/locationsManagement/:id/items/*');
  const isOnPromotion = !!useMatch('/locationsManagement/promotion');
  const isOnLocationShortcut = isOnMenuEditor || isOnPromotion;
  // Listy i formularze obu rodzajow rabatow sa "pod" ekranem wyboru /discount-types.
  const isOnDiscountList = !!useMatch('/discounts/*');
  const isOnCouponList = !!useMatch('/coupons/*');

  const secondary = Object.keys(resources).filter((n) => !RESOURCE_META[n]);

  // Pozycja zasobu renderuje sie tylko, gdy <Resource> jest zarejestrowany w App.tsx
  // (czesc z nich zalezy od roli).
  const resourceItem = (name: string, sx?: typeof resetActiveSx) =>
    resources[name] ? (
      <Menu.Item
        key={name}
        to={`/${name}`}
        primaryText={t(`resources.${name}`)}
        leftIcon={RESOURCE_META[name].icon}
        sx={sx}
      />
    ) : null;

  const sectionLabel = (label: string) => (
    <ListSubheader
      key={`section-${label}`}
      disableSticky
      sx={{
        lineHeight: '20px',
        mt: 'var(--space-2)',
        mb: 'var(--space-1)',
        px: '12px',
        bgcolor: 'transparent',
        color: 'var(--color-text-muted)',
        fontSize: 'var(--text-2xs)',
        fontWeight: 700,
        letterSpacing: 'var(--tracking-caps)',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </ListSubheader>
  );

  return (
    <>
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            minHeight: 'var(--appbar-height)',
            pl: 'var(--space-2)',
            pr: 'var(--space-6)',
          }}
        >
          <IconButton
            aria-label={t('common.close', { defaultValue: 'Close' })}
            onClick={() => setSidebarOpen(false)}
            sx={{ width: 40, height: 40, color: 'var(--color-text-secondary)' }}
          >
            <Close />
          </IconButton>
        </Box>
      )}
      <Menu
        sx={{
          '& .RaMenuItemLink-active': {
            backgroundColor: 'var(--color-primary-soft) !important',
            color: 'var(--color-primary-active) !important',
            boxShadow: 'inset 3px 0 0 0 var(--color-primary)',
            '& .RaMenuItemLink-icon': { color: 'var(--color-primary) !important' },
          },
          '& .MuiList-root': { gap: '2px' },
          /* MUI's MenuItem is `white-space: nowrap`, so long resource names were
           cut mid-word ("Zarządzanie lokaliz…") in the narrow mobile drawer.
           Let them wrap and give the row room for two lines. */
          '& .MuiMenuItem-root': { whiteSpace: 'normal', minHeight: 48 },
          /* react-admin renders the label as <Typography noWrap>, so it is the
           ellipsis, not the row. */
          '& .RaMenuItemLink-root .MuiTypography-root': {
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
            lineHeight: 1.3,
          },
        }}
      >
        {(resources.members || secondary.length > 0) && sectionLabel(t('menu.sections.accounts'))}
        {resourceItem('members')}
        {/* Zasoby bez przypisanej sekcji — zeby nowy <Resource> nie zniknal z menu. */}
        {secondary.map((name) => (
          <Menu.ResourceItem key={name} name={name} />
        ))}

        {sectionLabel(t('menu.sections.restaurant'))}
        {resourceItem('locationsManagement', isOnLocationShortcut ? resetActiveSx : undefined)}
        {/* Skrot do wlasnego menu siedzi pod "Zarzadzanie lokalizacjami", bo to ta
            sama lokalizacja widziana z drugiej strony. */}
        {restaurant && (
          <Menu.Item
            key="own-menu"
            to={`/locationsManagement/${restaurant.id}/items`}
            // Naglowek ekranu produktow czyta nazwe lokalizacji z `location.state`
            // (tak samo jak przejscie z listy lokalizacji), inaczej wyswietlilby
            // sie bez tytulu restauracji.
            state={{ locationId: restaurant.id, locationName: restaurant.name }}
            primaryText={t('locationsManagement.editMenu')}
            leftIcon={<MenuCardPencilIcon />}
          />
        )}
        <Menu.Item
          key="qr-codes"
          to="/qr-codes"
          primaryText={t('resources.qrCodes')}
          leftIcon={<QrCode2Outlined />}
        />
        {resourceItem('variantTemplates')}
        {resourceItem('orders')}
        {resourceItem('orderHistory')}

        {(resources.coupons || resources.discounts || canSendPromotion) &&
          sectionLabel(t('menu.sections.marketing'))}
        {canSendPromotion && (
          <Menu.Item
            key="promotion"
            to="/locationsManagement/promotion"
            primaryText={t('locationsManagement.sendPromotion')}
            leftIcon={<CampaignOutlined />}
          />
        )}
        {/* Jedna pozycja na oba rodzaje rabatow — prowadzi do ekranu wyboru
            (DiscountKindsPage), z ktorego idzie sie do listy rabatow albo kodow. */}
        {(resources.discounts || resources.coupons) && (
          <Menu.Item
            key="discount-types"
            to="/discount-types"
            primaryText={t('resources.discounts')}
            leftIcon={<PercentOutlined />}
            sx={isOnDiscountList || isOnCouponList ? forceActiveSx : undefined}
          />
        )}

        {isAdminOrSuperAdmin && [
          sectionLabel(t('common.tools', { defaultValue: 'Tools' })),
          <Menu.Item
            key="wolt-checker"
            to="/wolt-checker"
            primaryText={t('resources.woltChecker')}
            leftIcon={<LocalShippingOutlined />}
          />,
          <Menu.Item
            key="monthly-fees"
            to="/monthly-fees"
            primaryText={t('resources.monthlyFeeDefaults')}
            leftIcon={<PaymentsOutlined />}
          />,
        ]}

        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            m: 'var(--space-3)',
            p: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--color-bg-subtle)',
            border: '1px solid var(--color-border)',
          }}
        >
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: 'var(--text-xs)',
              fontWeight: 700,
              color: 'var(--color-text-secondary)',
            }}
          >
            <AppRegistrationOutlined sx={{ fontSize: 16, color: 'var(--color-primary)' }} />
            Menusso Admin
          </Typography>
          <Typography
            sx={{
              fontSize: 'var(--text-2xs)',
              color: 'var(--color-text-muted)',
              mt: '4px',
              lineHeight: 1.4,
            }}
          >
            {t('common.helpHint', { defaultValue: 'Need help? Contact support.' })}
          </Typography>
          <Box sx={{ mt: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box
              component="a"
              href="https://wa.me/48666668660"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: 'var(--text-2xs)',
                color: 'var(--color-primary)',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <WhatsApp sx={{ fontSize: 13, color: '#25D366' }} />
              +48 666 668 660
            </Box>
            <Box
              component="a"
              href="mailto:support@menusso.com"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: 'var(--text-2xs)',
                color: 'var(--color-primary)',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <MailOutlineOutlined sx={{ fontSize: 13 }} />
              support@menusso.com
            </Box>
          </Box>
        </Box>
      </Menu>
    </>
  );
};
